import React, { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
    useAddCardPhotoMutation,
    useGetCardQuery,
    usePostActivationLinkEmailMutation,
    usePostCardMutation,
    usePutCardMutation
} from '../../../../Redux/Api/Cards';
import { all, max, min } from '../../../../Utils/InValidation'
import { CardCreateItem, CardEditItem } from '../../../../Redux/Api/Request/Cards'
import { useAppSelector } from '../../../../Redux/store'
import Button from '../../../Shared/Form/Button'
import CalendarInput from '../../../Shared/Form/CalendarInput'
import StatusPopups from '../../../Shared/Form/StatusPopups'
import Form from '../../../Shared/Form/Form'
import Input, { FailedRequirements } from '../../../Shared/Form/Input'
import Title from '../../../Shared/Title'
import { Hydra } from '../../../../Utils/Hydra'
import BusyArea from '../../../Shared/BusyArea'
import Switch from '../../../Shared/Form/Switch'
import Card from "../../../Shared/Card/Card";
import Page404 from "../../Page404";
import { useLazyGetOrganisationSettingsQuery, useLazyGetTypeDataQuery } from "../../../../Redux/Api/Organisations";
import { usePostTypeMutation, useDeleteTypeMutation } from "../../../../Redux/Api/Types";
import { useTranslation } from "react-i18next";
import ImageInput from "../../../Shared/Form/ImageInput";
import { useUploadMutation } from "../../../../Redux/Api/Api";
import Map from '../../../Shared/Form/Map';
import PageMaxWidth from '../../../Shared/pageRenderers/PageMaxWidth';

import IconSVG from '../../../Assets/icon.svg?component'
import nlflag from '../../../../Assets/nl.svg'
import enflag from '../../../../Assets/en.svg'
import deflag from '../../../../Assets/de.svg'
import esflag from '../../../../Assets/es.svg'
import frflag from '../../../../Assets/fr.svg'
import { faLocationDot, faLocationDotSlash, faPlus, faXmarkCircle } from '@fortawesome/pro-solid-svg-icons';
import { Chip } from "@mui/material";
import Dropdown from '../../../Shared/Form/Dropdown';
import CardTypeSelectionList from '../../../Shared/Form/List/TypeSelectionList';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ModalContext } from '../../../Shared/Modal/Modal';

interface FormData {
    initials: string;
    lastname: string;
    email: string;
    numberOfPeople: number;
    activeFrom: string;
    activeUntil?: string;
    dateOfBirth?: string;
    location?: string;
    telephoneNumber?: string;
    department?: string;
    discount?: string;
    team?: string;
    countryCode?: string;
    membershipId: string;
    send: string;
    types?: string[];
}

const CardCreatePage = () => {

    const navigate = useNavigate()
    const { t, i18n } = useTranslation()

    const [postCard] = usePostCardMutation()
    const [sendActivationLink] = usePostActivationLinkEmailMutation()
    const [types, setTypes] = useState<Array<string>>([])
    const [newType, setNewType] = useState<string>('')
    const [postType] = usePostTypeMutation()
    const [deleteType] = useDeleteTypeMutation()
    const [putCard] = usePutCardMutation()
    const [uploadFile] = useUploadMutation()
    const [addPhoto] = useAddCardPhotoMutation()
    const [photo, setPhoto] = useState<Blob>()
    const [sendChecked, setSendChecked] = useState(false)
    const [showMap, setShowMap] = useState(false)
    const [location, setLocation] = useState<string>('')

    const org_id = useAppSelector((s) => s.organisation.id)!
    const [getOrganisationsSettings, { data: settings }] = useLazyGetOrganisationSettingsQuery()

    const [getOrganisationTypes, { data: orgTypes }] = useLazyGetTypeDataQuery();

    const [error, setError] = useState('')
    const [submitted, setSubmitted] = useState(false);
    const [busy, setBusy] = useState(false)
    const [formData, setFormData] = useState<FormData | null>(null);

    const modalContext = useContext(ModalContext)

    const toMYSQLDate = (datestring: string) => {
        const date = new Date(datestring)
        return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
    }

    useEffect(() => {
        if (org_id) {
            getOrganisationTypes({
                id: org_id
            })
            getOrganisationsSettings(org_id)
        }
    }, [org_id])

    const makeNumberValid = (min: number, max: number, input: number) => {
        if (input >= min && input <= max) return input
        if (input <= min) return min
        if (input >= max) return max
    }

    const requestDeleteType = async (id: string) => {
        if (id) {
            setBusy(true);
            await deleteType(id);

            getOrganisationTypes({ id: org_id });
            setBusy(false);
        }
    };

    const submit = async (data: {
        initials: string,
        lastname: string,
        image: File,
        email: string,
        numberOfPeople: number,
        activeFrom: string,
        activeUntil: string,
        dateOfBirth: string,
        location: string,
        telephoneNumber: string,
        department: string,
        discount: string,
        team: string,
        countryCode: string,
        membershipId: string,
        send: string,
        type: string
    }) => {
        setError('');
        setSubmitted(true);

        if (FailedRequirements(data, 'initials', 'lastname', 'email', 'activeFrom', 'membershipId')) {
            return setError(t("error") || '');
        }
        if (data.activeUntil === null || data.activeUntil === '') {
            data.activeUntil = '9999-01-01';
        }
        if (busy) return;

        setBusy(true);

        const currentDate = new Date();
        const activeFromDate = new Date(data.activeFrom);

        const formattedDate = formatDate(data.activeFrom);

        if (data.send === 'true' && activeFromDate > currentDate) {
            setFormData(data);
            setBusy(false);

            modalContext.withModal(
                { title: t('cards:list:warningTitle'), body: t('cards:list:activationEmailWarning', { activeFrom: formattedDate }) },
                handleDialogClose
            )();
            return;
        }

        await processFormSubmission(data);
    };

    function formatDate(activeFrom: string): string {
        const date = new Date(activeFrom);

        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();

        return `${day}-${month}-${year}`;
    }

    const handleDialogClose = async () => {
        if (formData) {
            setBusy(true);
            try {
                await processFormSubmission(formData);
            } finally {
                setBusy(false);
            }
        }
    };

    const processFormSubmission = async (data: {
        initials: string;
        lastname: string;
        email: string;
        activeFrom: string;
        activeUntil?: string;
        membershipId: string;
        dateOfBirth?: string;
        location?: string;
        telephoneNumber?: string;
        department?: string;
        discount?: string;
        team?: string;
        countryCode?: string;
        numberOfPeople?: number;
        send: string;
    }) => {
        try {
            const outData = {
                initials: data.initials,
                lastname: data.lastname,
                membershipId: data.membershipId,
                email: data.email,
                activeFrom: toMYSQLDate(data.activeFrom),
                activeUntil: toMYSQLDate(data.activeUntil || '9999-01-01'),
                dateOfBirth: data.dateOfBirth,
                location: data.location,
                telephoneNumber: data.telephoneNumber,
                department: data.department,
                discount: data.discount,
                team: data.team,
                countryCode: data.countryCode,
                numberOfPeople: settings?.familyCards ? Math.min(Math.max(data.numberOfPeople || 1, 1), settings.maxNumberOfPeople) : 1, // 🗣️🗣️🗣️ real typescript moment 🗿 -Tim Dieters
                types: [] as string[],
                emailActivation: data.send === 'true',
            };

            if (hasWhiteSpace(data.email)) {
                setError('Emailadres bevat spaties');
                scrollToTop();
                return;
            }

            for (let type of types) {
                let item = orgTypes?.find((x) => x.name === type);
                if (item) {
                    outData.types.push('/api/types/' + item.id);
                } else {
                    const id = await postType({
                        name: type.trim(),
                        organisation: org_id,
                        id: '',
                    }).unwrap();
                    outData.types.push('/api/types/' + id);
                }
            }

            const respData = await postCard([org_id, outData]).unwrap();
            const id = respData.id;

            if (photo) {
                const uploadablePhoto = new File([photo], 'uploaded_photo');
                const upload_id = await uploadFile({ file: uploadablePhoto }).unwrap();
                await addPhoto({ card_id: id, upload_id });
            }

            await putCard({ id, ...outData }).unwrap();


            const add = data.send === 'true' ? 'successWithMail' : 'successAdd';
            sessionStorage.setItem('successMessage', t('cards:edit:' + add) as string);
            const redirectUrl = `/${i18n.language}/dashboard/cards/edit/${id}#new-card`;
            navigate(redirectUrl);
        } catch (e) {
            let err = e as Hydra.Error;
            setError(err['hydra:description']);
        } finally {
            setBusy(false);
            sessionStorage.setItem('cardChanged', 'true');
        }
    }

    const hasWhiteSpace = (s: string) => {
        return s.indexOf(' ') >= 0;
    }

    const scrollToTop = () => {
        window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    };

    const makeUnique = (arr: Array<string>) => {
        const unique = [...new Set(arr)]
        return unique
    }

    const createNewType = () => {
        if (!newType || newType.toLocaleLowerCase() === 'notype') return

        const arrayOfTypes = types
        arrayOfTypes.push(newType)

        setTypes(makeUnique(arrayOfTypes))
        setNewType('')
    }

    const removeType = (type: string) => {
        setTypes(makeUnique(types.filter(t => t != type)))
    }

    const handleCrop = (blob: Blob | null) => {
        if (blob) {
            setPhoto(blob)
        }
    }
    const [number, setNumber] = useState('');
    const generateNumber = () => {
        const nummer = Math.floor(Math.random() * (999999 - 100000 + 1) + 100000);
        setNumber(nummer.toString());
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setNumber(e.target.value);
    };

    return (
        <PageMaxWidth pageType='form'>
            <Title text={t('cards:list:createCard')} />
            <BusyArea busy={busy}>
                <Form submit={submit} className='pb-24'>
                    <StatusPopups setText={setError} type='error' text={error} />

                    <div className='flex flex-col 2xl:flex-row gap-4'>
                        <div className='w-full'>
                            <Input
                                submitted={submitted}
                                required
                                invalidator={all(max(255))}
                                label={t('cards:list:initials')}
                                id='initials' />
                            <div className='flex gap-4 w-full'>
                                <Input
                                    className='w-full'
                                    submitted={submitted}
                                    required
                                    invalidator={all(min(2), max(255))}
                                    label={t('cards:list:lastname')}
                                    id='lastname' />
                            </div>
                            <Input
                                tooltip={t('cards:list:warningEmail') as string}
                                submitted={submitted ? true : false}
                                required
                                invalidator={all(max(255))}
                                label={t('cards:list:email')}
                                type='email'
                                id='email' />
                        </div>
                        {settings?.photo &&
                            <div className='w-full 2xl:w-[700px] mb-6 2xl:mb-0'>
                                <ImageInput onCrop={handleCrop} />
                            </div>
                        }
                    </div>

                    {settings?.familyCards ?
                        <Input
                            submitted={submitted}
                            required
                            type='number'
                            initial='1'
                            label={t('cards:edit:numberOfPeople')}
                            sublabel={'Max ' + settings.maxNumberOfPeople}
                            id='numberOfPeople' /> : null
                    }
                    <CalendarInput
                        submitted={submitted}
                        className='mb-6'
                        required
                        label={t('cards:list:activeFrom')}
                        id='activeFrom' />

                    <CalendarInput
                        className='mb-6'
                        label={t('cards:list:activeUntil')}
                        sublabel={t('cards:list:optional') as string}
                        id='activeUntil' />
                    <Input
                        submitted={submitted}
                        required
                        value={number}
                        onChange={handleInputChange}
                        invalidator={all(min(2), max(255))}
                        label={t('cards:list:number')}
                        id='membershipId'
                        className='mb-[-2px]' />
                    <button className='mb-6 text-sm hover:text-blue-700 underline' type="button" onClick={generateNumber}>{t('cards:edit:generateId')}</button>

                    <Input
                        sublabel={t('cards:list:optional') as string}
                        label={t('cards:list:dateOfBirth')}
                        id='dateOfBirth' />

                    <div className='flex gap-2'>
                        <Input
                            label={t('cards:list:location')}
                            onChange={(e) => {
                                setLocation((e.target as HTMLInputElement).value);
                            }}
                            sublabel={t('cards:list:optional') as string}
                            value={location}
                            id='location'
                            className="w-full"
                        />
                        <div className='mt-[29px]'>
                            <Button
                                icon={showMap ? faLocationDotSlash : faLocationDot}
                                action={() => setShowMap(!showMap)}
                                nosubmit
                                title={t('cards:list:location')}
                            />
                        </div>
                    </div>

                    {showMap && (
                        <Map
                            setBusy={setBusy}
                            onChange={(e) => {
                                setLocation(e.locationName);
                            }}
                            initialLocation={location}
                        />
                    )}

                    <Input
                        sublabel={t('cards:list:optional') as string}
                        label={t('cards:list:phone')}
                        id='telephoneNumber' />
                    <Input
                        sublabel={t('cards:list:optional') as string}
                        label={t('cards:list:department')}
                        id='department' />

                    <Input
                        sublabel={t('cards:list:optional') as string}
                        label={t('cards:list:discount')}
                        id='discount' />

                    <Input
                        sublabel={t('cards:list:optional') as string}
                        label={t('cards:list:team')}
                        id='team' />

                    <Dropdown
                        noSearch
                        noMarginBottom
                        className='mb-4'
                        label={t('cards:list:countryCode')}
                        id='countryCode'
                        initial='NL'
                        dropdownFullHeight
                        options={{
                            'NL': String(t('organisation:theme:NL')),
                            'EN': String(t('organisation:theme:ENG')),
                            'DE': String(t('organisation:theme:DE')),
                            'FR': String(t('organisation:theme:FR')),
                            'ES': String(t('organisation:theme:ES'))
                        }}
                        optionsImages={{
                            'NL': nlflag,
                            'EN': enflag,
                            'DE': deflag,
                            'FR': frflag,
                            'ES': esflag
                        }}
                    />

                    <div className="mb-4 flex flex-col md:flex-row lg:flex-col xl:flex-row gap-6">
                        <div className={`relative min-h-16 w-full md:w-[70%] lg:w-full xl:w-[70%]`}>
                            <p className='mb-1'>{t('cards:list:types')}</p>
                            <div className="flex gap-2 flex-wrap max-w-full">
                                {types.length > 0 ? (
                                    types.map((type, i) => (type.trim() !== "" && (
                                        <Chip
                                            key={i}
                                            label={type}
                                            onDelete={() => removeType(type)}
                                            deleteIcon={<FontAwesomeIcon icon={faXmarkCircle} />}
                                            variant="outlined"
                                            className='cursor-default'
                                        />
                                    )))
                                ) : (
                                    <p className="absolute top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 text-[#5B6C79] text-md">
                                        {t('cards:edit:noType') as string}
                                    </p>
                                )}
                            </div>
                        </div>

                        <CardTypeSelectionList
                            className='w-full md:w-[30%] lg:w-full xl:w-[30%]'
                            orgTypes={orgTypes}
                            types={types}
                            newType={newType}
                            setNewType={setNewType}
                            createNewType={createNewType}
                            removeType={removeType}
                            setTypes={setTypes}
                            makeUnique={makeUnique}
                            onDelete={requestDeleteType}
                        />
                    </div>

                    <Switch id='send' onClick={(value) => setSendChecked(value)} label={t('cards:list:sendMail?')} />
                    <Button icon={faPlus} iconright title={t('cards:list:create')} />
                </Form>
            </BusyArea>
        </PageMaxWidth>
    )
}


export default CardCreatePage
